import { RydBookmark } from "./bookmark";
import { AbstractHorizontalContainer, ContainerAtom, HorizontalContainer, HorizontalNoteContainer } from "./containers";
import { Note } from "./core";

export type LoopIterationSpec = number | 'inf';

export type LoopPlaybackSetup = {
    isLooping: boolean;
    start?: RydBookmark;
    end?: RydBookmark;
};

export class LoopingContainer<T> implements HorizontalContainer<T> {

    private length: number;

    // loop properties
    private baseContainer: HorizontalContainer<T>;
    private loopStart: RydBookmark;
    private loopEnd: RydBookmark;
    private loopLimit: LoopIterationSpec;

    constructor({
        baseContainer,
        loopStart,
        loopEnd,
        loopLimit = 'inf'
    }: {
        baseContainer: HorizontalContainer<T>,
        loopStart: RydBookmark,
        loopEnd: RydBookmark,
        loopLimit?: LoopIterationSpec
    }) {
        this.baseContainer = baseContainer;
        this.setLoop(loopStart, loopEnd);
        this.loopLimit = loopLimit;
    }

    getLength(): number {
        if (this.isInfiniteLoop()) {
            return Infinity;
        }

        return (this.loopLimit as number) * (this.loopEnd.position - this.loopStart.position)
    }

    getPPQ(): number {
        return this.baseContainer.getPPQ();
    }

    getPickupLength(): number {
        return 0;
    }

    getBaseContainer(): HorizontalContainer<T> {
        return this.baseContainer;
    }
    getLoopStart(): RydBookmark {
        return this.loopStart;
    }
    getLoopEnd(): RydBookmark {
        return this.loopEnd;
    }
    getLoopLimit(): LoopIterationSpec {
        return this.loopLimit;
    }
    setLoopStart(loopStart: RydBookmark): void {
        this.setLoop(loopStart, this.loopEnd);
    }
    setLoopEnd(loopEnd: RydBookmark): void {
        this.setLoop(this.loopStart, loopEnd);
    }
    setLoopLimit(loopLimit: number): void {
        this.loopLimit = loopLimit;
    }
    isInfiniteLoop(): boolean {
        return this.loopLimit === 'inf' || this.loopLimit === -1;
    }
    setLoop(
        loopStart: RydBookmark,
        loopEnd: RydBookmark
    ): void {
        if (loopEnd.position <= loopStart.position) {
            throw new Error("Loop end must be greater than loop start.");
        }
        this.loopStart = loopStart;
        this.loopEnd = loopEnd;
    }

    get(step: number): ContainerAtom<T>[] {
        const loopLength = this.loopEnd.position - this.loopStart.position;
        return this.getGrid()[step % loopLength] || [];
    }

    getGrid(
        compact: boolean = true,
        range?: { start: number, length: number }
    ): ContainerAtom<T>[][] {
        if (this.isInfiniteLoop()) {
            throw new Error("Cannot get grid of infinite loop.");
        }
        const baseGrid = this.baseContainer.getGrid(false);
        const loopLength = this.loopEnd.position - this.loopStart.position;
        let baseResult: ContainerAtom<T>[][] = this.baseContainer.getGrid(compact, {
            start: this.loopStart.position,
            length: loopLength
        });
        if (range) {
            const end = range.start + range.length;
            baseResult = baseResult.filter((_, i) => i >= range.start && i < end);
        }
        return Array(this.loopLimit as number).fill(0).flatMap(() => baseResult);
    }

    search(start: number, end: number): ContainerAtom<T>[] {
        throw new Error("Method not implemented.");
    }
}
